import { i18nError, UnauthorizedError } from "@/@types/api-error";
import { DocumentUsecase } from "@/usecases/document/DocumentUsecase";
import { useLoading } from "@/utils/hooks/loading.hooks";
import { usePrivatePageContext } from "@/utils/hooks/private-page.hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DataSchema } from "common";
import { useDisplay } from "@/utils/hooks/display.hooks";
import { bookmarksAtom } from "@/atoms/bookmark";
import { useAtom } from "jotai";
import { BookmarkUsecase } from "@/usecases/bookmark/BookmarkUsecase";
import { useDocumentChat } from "./DocumentChat/DocumentChat.hooks";
import { generateRandomKey } from "@/utils/generate-ramdom-key";

type DocumentPageParams = {
  documentId: string;
};

export const useDocumentPage = () => {
  const { documentId } = useParams<DocumentPageParams>();
  const encodedDocumentId = location.pathname.split("/")[2];
  const urlSearchParams = new URLSearchParams(location.search);
  const page = urlSearchParams.get("page") ?? "";
  const timestamp = urlSearchParams.get("timestamp") ?? "";

  const { t } = useTranslation();
  const { apiClient, logout, user } = usePrivatePageContext();
  const totalCategories = user.categories;
  const totalLanguages = user.languages;
  const [bookmarks, setBookmarks] = useAtom(bookmarksAtom);
  const { loading, showLoading, hideLoading } = useLoading();
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState<boolean>(false);
  const [documentData, setDocumentData] = useState<DataSchema.Document>();
  const { type: deviceType, breakpoint: deviceBreakpoint } = useDisplay();
  const canPreview = !["mobile", "tablet"].includes(deviceType);
  const [totalDocumentGood, setTotalDocumentGood] = useState(0);
  const [isGoodDocument, setIsGoodDocument] = useState(false);
  // NOTE: 640px以上+previewされてない場合は初期表示だけChatを開く
  const [openDocumentChat, setOpenDocumentChat] = useState(() => deviceBreakpoint !== "mobile");
  // NOTE: 1024px以上の幅の場合初期表示でDetailView right sidebarを開いて未満の場合は閉じる
  const [openDocumentDetail, setOpenDocumentDetail] = useState(() => window.innerWidth >= 1024);
  const [forbiddenError, setForbiddenError] = useState(false);

  const chatId = useMemo(() => generateRandomKey(), []);
  const documentChatHooks = useDocumentChat(encodedDocumentId, chatId);

  const iframeSrcUrl = useMemo(() => {
    let iframeSrcUrl = documentData?.sourceUrl ?? "";
    if (page.length > 0) {
      const params = new URLSearchParams();
      params.append("page", page);
      iframeSrcUrl += `#${params.toString()}`;
    }
    return iframeSrcUrl;
  }, [documentData, page]);

  const getDocumentGood = useCallback(() => {
    const documentId = encodedDocumentId;
    DocumentUsecase.documentGood(apiClient, documentId)
      .then((response) => {
        setTotalDocumentGood(response.total);
        setIsGoodDocument(response.value === 1);
      })
      .catch((error) => {
        if (error instanceof UnauthorizedError) {
          logout();
          return;
        }
      });
  }, [apiClient, encodedDocumentId, logout]);

  const onClickGood = useCallback(() => {
    const documentId = encodedDocumentId;
    if (isGoodDocument) {
      DocumentUsecase.deleteDocumentGood(apiClient, documentId);
      setTotalDocumentGood(totalDocumentGood - 1);
      setIsGoodDocument(false);
    } else {
      DocumentUsecase.addDocumentGood(apiClient, documentId);
      setTotalDocumentGood(totalDocumentGood + 1);
      setIsGoodDocument(true);
    }
  }, [apiClient, encodedDocumentId, isGoodDocument, totalDocumentGood]);

  const getDocument = useCallback(() => {
    if (!documentId) {
      return;
    }
    showLoading();
    DocumentUsecase.document(apiClient, documentId)
      .then((response) => {
        setDocumentData(response);
      })
      .catch((error) => {
        if (error instanceof UnauthorizedError) {
          logout();
          return;
        }
        if (error instanceof i18nError) {
          if (error.i18nKey === "api_error_forbidden") {
            setForbiddenError(true);
            return;
          }
          setApiErrorMessage(t(error.i18nKey));
        } else {
          setApiErrorMessage(t("api_error_panic"));
        }
        setApiErrorDialogOpen(true);
      })
      .finally(() => {
        hideLoading();
      });
  }, [apiClient, documentId, hideLoading, logout, showLoading, t]);

  const bookmarkOn = useMemo(() => {
    const pageNo = Number(page);
    const result = bookmarks.some((bookmark) => {
      return bookmark.documentId === encodedDocumentId && bookmark.pageNo === pageNo;
    });
    return result;
  }, [bookmarks, encodedDocumentId, page]);

  const onClickBookmark = useCallback(() => {
    if (!documentData) {
      return;
    }

    const documentId = encodedDocumentId;
    const documentTitle = documentData.title;
    const pageNo = Number(page);

    if (bookmarkOn) {
      BookmarkUsecase.deleteBookmark(apiClient, documentId, pageNo);
      const updatedBookmarks = bookmarks.filter((bookmark) => {
        return !(bookmark.documentId === documentId && bookmark.pageNo === pageNo);
      });
      setBookmarks(updatedBookmarks);
    } else {
      const newBookmark = { documentId, documentTitle, pageNo };
      BookmarkUsecase.addBookmark(apiClient, newBookmark);
      const updatedBookmarks = [newBookmark, ...bookmarks];
      setBookmarks(updatedBookmarks);
    }
  }, [apiClient, bookmarkOn, bookmarks, documentData, encodedDocumentId, page, setBookmarks]);

  const onClickOpenDocumentChat = useCallback(() => {
    setOpenDocumentChat(true);
  }, []);

  const onClickCloseDocumentChat = useCallback(() => {
    setOpenDocumentChat(false);
  }, []);

  const onClickOpenDocumentDetail = useCallback(() => {
    setOpenDocumentDetail(true);
  }, []);

  const onClickCloseDocumentDetail = useCallback(() => {
    setOpenDocumentDetail(false);
  }, []);

  const relatedDocuments = useMemo(
    () =>
      documentData?.relatedDocuments?.reduce(
        (groups, item) => {
          for (const category of item.categories) {
            const g = groups[category] ?? [];
            g.push(item);
            groups[category] = g;
          }
          return groups;
        },
        {} as Record<string, DataSchema.DocumentSummary[]>,
      ),
    [documentData?.relatedDocuments],
  );

  useEffect(() => {
    getDocument();
    getDocumentGood();
  }, [getDocument, getDocumentGood]);

  useEffect(() => {
    documentChatHooks.scrollToAnchor();
  }, [openDocumentChat, documentChatHooks.messages, documentChatHooks]);

  return {
    t,
    loading,
    documentData,
    apiErrorMessage,
    apiErrorDialogOpen,
    setApiErrorDialogOpen,
    iframeSrcUrl,
    bookmarkOn,
    onClickBookmark,
    totalDocumentGood,
    isGoodDocument,
    onClickGood,
    onClickOpenDocumentChat,
    onClickCloseDocumentChat,
    openDocumentChat,
    documentChatHooks,
    openDocumentDetail,
    onClickOpenDocumentDetail,
    onClickCloseDocumentDetail,
    canPreview,
    page,
    totalCategories,
    totalLanguages,
    timestamp,
    relatedDocuments,
    firLink: user.firLink,
    forbiddenError,
  };
};
